import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import { BASE, URLS, APIS, SF_MANL_INFO_FORM } from "@/tz/go/egaz/osci/ptl/clntsport/manl/services/OsciManlService";

/*
 * Manage manual information registration
 * !== 매뉴얼정보관리 등록 ==!
 */
export const OSCI0107003S = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0001", "auth_osci0002", "auth_osci0003"],
    }); // Page Unique identifier !== 화면 고유 식별자 ==!
    const pgeTitle = "Manage manual information registration"; // Page Title !== 페이지 타이틀 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { scrnId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0088" },
            { area: "comnCd", comnCd: "OSC0095" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Manage manual information Form !== 매뉴얼정보관리 Form ==!
        manlInfoForm: useForm({
            defaultSchema: SF_MANL_INFO_FORM,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //  !== 화면 ID로 매뉴얼 정보를 가져온다==!
        getManlInfo: useFetch({
            api: () => APIS.getManlInfo(scrnId),
            enabled: comnUtils.isEmpty(form.manlInfoForm.errors),
            onSuccess: (data) => {
                form.manlInfoForm.setValues(data.manlDtlInfo.content);
            },
            onError: (error: any) => {
                const { errorMessage } = error.response.data;
                modal.openModal({ content: errorMessage });
            },
        }),

        updateManlInfo: useFetch({
            api: (info: any) => APIS.updateManlInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00070",
                    onCancel: () => {
                        navigate(URLS.osci0107001q);
                    },
                }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00039" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),

        deleteManlInfo: useFetch({
            api: (scrnId: any) => APIS.deleteManlInfo(scrnId),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onCancel: () => {
                        navigate(URLS.osci0107001q);
                    },
                }); // Processed successfully. !== 성공적으로 처리 되었습니다.==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00039" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onSearchScrnClick: () => {
            fetch.getManlInfo.fetch(0);
        },

        onUpdateBtnClick: () => {
            if (!comnUtils.isEmpty(form.manlInfoForm.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.manlInfoForm.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.osc.00064", // Confirm update? !== 수정하시겠습니까? ==!
                        onConfirm: () => fetch.updateManlInfo.fetch(form.manlInfoForm.getValues()),
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                    console.log(comnUtils.isEmpty(form.manlInfoForm.errors), form.manlInfoForm.isSubmitted);
                },
            )();
        },

        onDeleteBtnClick: () => {
            if (!comnUtils.isEmpty(form.manlInfoForm.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.manlInfoForm.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.osc.00062", // Confirm update? !== 수정하시겠습니까? ==!
                        onConfirm: () => fetch.deleteManlInfo.fetch(form.manlInfoForm.getValue("scrnId")),
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                    console.log(comnUtils.isEmpty(form.manlInfoForm.errors), form.manlInfoForm.isSubmitted);
                },
            )();
        },
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        edit: {},
    };

    useEffect(() => {
        form.manlInfoForm.setEditable("scrnId", false);

        if (menuId != "menu_osci0148") {
            form.manlInfoForm.setEditable(false);
        }
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.manlInfoForm.schema.scrnId} />
                            <Group.Control {...form.manlInfoForm.schema.scrnNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.manlInfoForm.schema.bsopClsfCd} />
                            <Group.Control {...form.manlInfoForm.schema.ptlTpCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.manlInfoForm.schema.manlFileIdTz} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.manlInfoForm.schema.manlFileIdEn} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.manlInfoForm.schema.manlFileIdKo} controlSize={10} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0107001q);
                        }}
                    />
                </Layout.Left>

                <Layout.Right>
                    <Button
                        hidden={menuId != "menu_osci0148"}
                        role="save"
                        onClick={() => {
                            handler.onUpdateBtnClick();
                        }}
                    >
                        {t("B_EDIT")}
                    </Button>

                    <Button
                        hidden={menuId != "menu_osci0148"}
                        role="delete"
                        onClick={() => {
                            handler.onDeleteBtnClick();
                        }}
                    >
                        {t("B_DEL")}
                    </Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
